import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import { firstValueFrom, from, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/auth/auth.service';
import qnsbnurlAPI from './qn-sbn-API-url';

// Served locally (copied from pdfjs-dist via angular.json assets) to satisfy the app's CSP script-src.
pdfjsLib.GlobalWorkerOptions.workerSrc = 'assets/pdfjs/pdf.worker.min.mjs';

export interface TranslationRequest {
  pimid: string;
  text: string;
  sourceLanguage: string;
  targetLanguage: string;
}

export interface TranslationResponse {
  translatedText: string;
  sourceLanguage: string;
  targetLanguage: string;
}

@Injectable({ providedIn: 'root' })
export class TranslationService {
  public constructor(private http: HttpClient, private authService: AuthService) {}

  public getTranslation(request: TranslationRequest): Observable<TranslationResponse> {
    return this.http.post<TranslationResponse>(this.getTranslateUrl(), request, {
      headers: this.getHeaders(),
    });
  }

  // Fetches the PIOS PDF and returns its extracted plain text.
  public getDocumentText(pimid: string): Observable<string> {
    return from(this.extractPdfText(pimid));
  }

  private async extractPdfText(pimid: string): Promise<string> {
    const url = this.getApiBaseUrl() + qnsbnurlAPI.downloadDocument + pimid;
    const buffer = await firstValueFrom(
      this.http.get(url, { headers: this.getHeaders(), responseType: 'arraybuffer' }),
    );

    const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(buffer) }).promise;
    const pages: string[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      pages.push(content.items.map((item: any) => ('str' in item ? item.str : '')).join(' '));
    }
    return pages.join('\n').trim();
  }

  private getHeaders(): HttpHeaders {
    const rawToken = this.authService.getIdToken();
    const token = rawToken ? String(rawToken).replace(/^Bearer\s+/i, '').trim() : '';
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  private getApiBaseUrl(): string {
    return environment.appApiUrl || `${window.location.origin}/api/`;
  }

  private getTranslateUrl(): string {
    return this.getApiBaseUrl() + qnsbnurlAPI.translate;
  }
}

